<script lang="ts" setup>
import { PhPlus, PhTrash } from '@phosphor-icons/vue';
import { formatNumber } from 'cesg-shared';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StateInput, {
  type StateInputMode,
} from '../../../../inputs/state-input/state-input.vue';

export type LiftTableEntryColumnKey =
  | 'number'
  | 'description'
  | 'dueDate'
  | 'amount'
  | 'retention'
  | 'amountDue'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export interface LiftTableEntry {
  id: string;
  number: number;
  description?: string;
  dueDate?: Date;
  amount: number;
  retention: number;
  amountDue: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftTableColumn {
  key: LiftTableEntryColumnKey;
  label: string;
  state: LiftTableColumnState;
  getValue: (entry: LiftTableEntry) => string;
}

export interface LiftTableSubtotalRow {
  id: string;
  label: string;
  amount: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftTableAtaRow {
  id: string;
  label: string;
  amount: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

type LiftTableColumnState = StateInputMode;
export interface LiftTableSubtotalColumn {
  key: LiftTableSubtotalColumnKey;
  state: LiftTableColumnState;
  getValue: (row: LiftTableSubtotalRow) => string;
}

export type LiftTableAtaColumnKey =
  | 'label'
  | 'amount'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export interface LiftTableAtaColumn {
  key: LiftTableAtaColumnKey;
  state: LiftTableColumnState;
  getValue: (row: LiftTableAtaRow) => string;
}

export type LiftTableSubtotalColumnKey =
  | 'label'
  | 'amount'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export type LiftTableComponentElementState = 'hidden' | 'disabled' | 'visible';
export type LiftTableInputState = 'edit' | 'read-only' | 'hidden';

export interface LiftTableComponentState {
  deleteEntry: LiftTableComponentElementState;
  deleteSubtotal: LiftTableComponentElementState;
  deleteAta: LiftTableComponentElementState;
  addLift: LiftTableComponentElementState;
  addRow: LiftTableComponentElementState;
  addAta: LiftTableComponentElementState;
  contractSum: LiftTableInputState;
  retentionPercentage: LiftTableInputState;
  retentionAmount: LiftTableInputState;
  entreprenadsumma: LiftTableInputState;
  sectionCard: {
    edit: LiftTableComponentElementState;
    title: LiftTableInputState;
  };
}

interface LiftTableProps {
  columns: Partial<Record<LiftTableEntryColumnKey, LiftTableColumn>>;
  subtotalColumns: Partial<
    Record<LiftTableSubtotalColumnKey, LiftTableSubtotalColumn>
  >;
  ataColumns: Partial<Record<LiftTableAtaColumnKey, LiftTableAtaColumn>>;
  entries: LiftTableEntry[];
  subtotalRows: LiftTableSubtotalRow[];
  ataRows: LiftTableAtaRow[];
  totalAmount: number;
  totalRetention: number;
  totalAmountDue: number;
  componentState: LiftTableComponentState;
}

const emit = defineEmits<{
  'add-entry': [];
  'delete-entry': [id: string];
  'update-entry': [id: string, field: LiftTableEntryColumnKey, value: string];
  'add-subtotal': [];
  'delete-subtotal': [id: string];
  'update-subtotal': [
    id: string,
    field: 'label' | 'amount' | 'invoicedDate' | 'paymentDate' | 'comment',
    value: string,
  ];
  'add-ata': [];
  'delete-ata': [id: string];
  'update-ata': [id: string, field: LiftTableAtaColumnKey, value: string];
}>();

const props = defineProps<LiftTableProps>();

const { t } = useI18n();

const TRAILING_COLUMN_KEYS: LiftTableEntryColumnKey[] = [
  'invoicedDate',
  'paymentDate',
  'comment',
];

const visibleColumnCount = computed(() => Object.keys(props.columns).length);

const addRowColspan = computed(
  () =>
    visibleColumnCount.value +
    (props.componentState.deleteEntry !== 'hidden' ? 1 : 0),
);

const totalsTrailingColspan = computed(() => {
  return TRAILING_COLUMN_KEYS.filter((k) => k in props.columns).length;
});
</script>

<template>
  <div class="table-wrapper">
    <table class="table is-striped is-hoverable mb-0">
      <thead>
        <tr>
          <th
            v-if="props.componentState.deleteEntry !== 'hidden'"
            class="col-action"
          ></th>
          <th v-if="props.columns.number" class="col-number">
            {{ props.columns.number.label }}
          </th>
          <th v-if="props.columns.description" class="col-description">
            {{ props.columns.description.label }}
          </th>
          <th v-if="props.columns.dueDate" class="col-date">
            {{ props.columns.dueDate.label }}
          </th>
          <th v-if="props.columns.amount" class="col-amount">
            {{ props.columns.amount.label }}
          </th>
          <th v-if="props.columns.retention" class="col-amount">
            {{ props.columns.retention.label }}
          </th>
          <th v-if="props.columns.amountDue" class="col-amount">
            {{ props.columns.amountDue.label }}
          </th>
          <th v-if="props.columns.invoicedDate" class="col-date">
            {{ props.columns.invoicedDate.label }}
          </th>
          <th v-if="props.columns.paymentDate" class="col-date">
            {{ props.columns.paymentDate.label }}
          </th>
          <th v-if="props.columns.comment">
            {{ props.columns.comment.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="entry in props.entries" :key="entry.id">
          <td
            v-if="props.componentState.deleteEntry !== 'hidden'"
            class="col-action"
          >
            <button
              class="row-delete-btn"
              :disabled="props.componentState.deleteEntry === 'disabled'"
              @click="emit('delete-entry', entry.id)"
            >
              <PhTrash :size="13" />
            </button>
          </td>
          <td v-if="props.columns.number" class="col-number entry-number">
            {{ t('projects.liftPlanUE.liftTable.liftPrefix') }}
            <StateInput
              :mode="
                props.columns.number.state === 'edit' ? 'edit' : 'read-only'
              "
              class="inline-input number-input"
              inputmode="numeric"
              :model-value="props.columns.number.getValue(entry)"
              @update:model-value="
                emit('update-entry', entry.id, 'number', $event)
              "
            />
          </td>
          <td v-if="props.columns.description" class="col-description">
            <StateInput
              :mode="
                props.columns.description.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input description-input"
              :model-value="props.columns.description.getValue(entry)"
              @update:model-value="
                emit('update-entry', entry.id, 'description', $event)
              "
            />
          </td>
          <td v-if="props.columns.dueDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.columns.dueDate.state === 'edit' ? 'edit' : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="props.columns.dueDate.getValue(entry)"
              @update:model-value="
                emit('update-entry', entry.id, 'dueDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.amount" class="col-amount">
            <span class="amount-field">
              <StateInput
                :mode="
                  props.columns.amount.state === 'edit' ? 'edit' : 'read-only'
                "
                class="inline-input amount-input"
                inputmode="numeric"
                :model-value="props.columns.amount.getValue(entry)"
                @update:model-value="
                  (event) => {
                    emit('update-entry', entry.id, 'amount', event);
                  }
                "
              />
              kr
            </span>
          </td>
          <td v-if="props.columns.retention" class="col-amount">
            <span class="amount-field">
              <StateInput
                :mode="
                  props.columns.retention.state === 'edit'
                    ? 'edit'
                    : 'read-only'
                "
                class="inline-input amount-input"
                inputmode="numeric"
                :model-value="props.columns.retention.getValue(entry)"
                @update:model-value="
                  emit('update-entry', entry.id, 'retention', $event)
                "
              />
              kr
            </span>
          </td>
          <td v-if="props.columns.amountDue" class="col-amount">
            <span class="amount-field">
              <StateInput
                :mode="
                  props.columns.amountDue.state === 'edit'
                    ? 'edit'
                    : 'read-only'
                "
                class="inline-input amount-input"
                inputmode="numeric"
                :model-value="props.columns.amountDue.getValue(entry)"
                @update:model-value="
                  emit('update-entry', entry.id, 'amountDue', $event)
                "
              />
              kr
            </span>
          </td>
          <td v-if="props.columns.invoicedDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.columns.invoicedDate.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="props.columns.invoicedDate.getValue(entry)"
              @update:model-value="
                emit('update-entry', entry.id, 'invoicedDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.paymentDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.columns.paymentDate.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="props.columns.paymentDate.getValue(entry)"
              @update:model-value="
                emit('update-entry', entry.id, 'paymentDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.comment" class="comment-cell">
            <StateInput
              :mode="
                props.columns.comment.state === 'edit' ? 'edit' : 'read-only'
              "
              class="inline-input comment-input"
              :model-value="props.columns.comment.getValue(entry)"
              @update:model-value="
                emit('update-entry', entry.id, 'comment', $event)
              "
            />
          </td>
        </tr>
        <tr v-if="props.componentState.addLift !== 'hidden'" class="add-row">
          <td :colspan="addRowColspan" class="add-row-cell">
            <button
              class="row-add-btn"
              :disabled="props.componentState.addLift === 'disabled'"
              @click="emit('add-entry')"
            >
              <PhPlus :size="13" />
              {{ t('projects.liftPlanUE.liftTable.addLift') }}
            </button>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="totals-row">
          <td
            v-if="props.componentState.deleteEntry !== 'hidden'"
            class="col-action"
          ></td>
          <td colspan="3">{{ t('projects.liftPlanUE.liftTable.total') }}</td>
          <td class="col-amount">{{ formatNumber(props.totalAmount) }} kr</td>
          <td class="col-amount">
            {{ formatNumber(props.totalRetention) }} kr
          </td>
          <td v-if="props.columns.amountDue" class="col-amount">
            {{ formatNumber(props.totalAmountDue) }} kr
          </td>
          <td :colspan="totalsTrailingColspan"></td>
        </tr>
        <tr
          v-for="row in props.subtotalRows"
          :key="row.id"
          class="subtotals-row"
        >
          <td
            v-if="props.componentState.deleteSubtotal !== 'hidden'"
            class="col-action"
          >
            <button
              class="row-delete-btn"
              :disabled="props.componentState.deleteSubtotal === 'disabled'"
              @click="emit('delete-subtotal', row.id)"
            >
              <PhTrash :size="13" />
            </button>
          </td>
          <td colspan="4" class="subtotal-label-cell">
            <StateInput
              :mode="
                props.subtotalColumns.label?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input label-input"
              :model-value="
                props.subtotalColumns.label?.getValue(row) ?? row.label
              "
              @update:model-value="
                emit('update-subtotal', row.id, 'label', $event)
              "
            />
          </td>
          <td class="col-amount">
            <span class="amount-field">
              <StateInput
                :mode="
                  props.subtotalColumns.amount?.state === 'edit'
                    ? 'edit'
                    : 'read-only'
                "
                class="inline-input amount-input"
                inputmode="numeric"
                :model-value="
                  props.subtotalColumns.amount?.getValue(row) ??
                  formatNumber(row.amount)
                "
                @update:model-value="
                  emit('update-subtotal', row.id, 'amount', $event)
                "
              />
              kr
            </span>
          </td>
          <td v-if="props.columns.amountDue"></td>
          <td v-if="props.columns.invoicedDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.subtotalColumns.invoicedDate?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="
                props.subtotalColumns.invoicedDate?.getValue(row) ?? ''
              "
              @update:model-value="
                emit('update-subtotal', row.id, 'invoicedDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.paymentDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.subtotalColumns.paymentDate?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="
                props.subtotalColumns.paymentDate?.getValue(row) ?? ''
              "
              @update:model-value="
                emit('update-subtotal', row.id, 'paymentDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.comment" class="comment-cell">
            <StateInput
              :mode="
                props.subtotalColumns.comment?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input comment-input"
              :model-value="props.subtotalColumns.comment?.getValue(row) ?? ''"
              @update:model-value="
                emit('update-subtotal', row.id, 'comment', $event)
              "
            />
          </td>
        </tr>
        <tr
          v-if="props.componentState.addRow !== 'hidden'"
          class="add-subtotal-row"
        >
          <td :colspan="addRowColspan" class="add-row-cell">
            <button
              class="row-add-btn"
              :disabled="props.componentState.addRow === 'disabled'"
              @click="emit('add-subtotal')"
            >
              <PhPlus :size="13" />
              {{ t('projects.liftPlanUE.liftTable.addRow') }}
            </button>
          </td>
        </tr>
        <tr
          v-if="
            props.componentState.addAta !== 'hidden' || props.ataRows.length > 0
          "
          class="ata-section-header-row"
        >
          <th :colspan="addRowColspan">
            {{ t('projects.liftPlanUE.liftTable.ataHeader') }}
          </th>
        </tr>
        <tr
          v-for="row in props.ataRows"
          :key="row.id"
          class="subtotals-row ata-row"
        >
          <td
            v-if="props.componentState.deleteAta !== 'hidden'"
            class="col-action"
          >
            <button
              class="row-delete-btn"
              :disabled="props.componentState.deleteAta === 'disabled'"
              @click="emit('delete-ata', row.id)"
            >
              <PhTrash :size="13" />
            </button>
          </td>
          <td colspan="4" class="subtotal-label-cell">
            <StateInput
              :mode="
                props.ataColumns.label?.state === 'edit' ? 'edit' : 'read-only'
              "
              class="inline-input label-input"
              :model-value="props.ataColumns.label?.getValue(row) ?? row.label"
              @update:model-value="emit('update-ata', row.id, 'label', $event)"
            />
          </td>
          <td class="col-amount">
            <span class="amount-field">
              <StateInput
                :mode="
                  props.ataColumns.amount?.state === 'edit'
                    ? 'edit'
                    : 'read-only'
                "
                class="inline-input amount-input"
                inputmode="text"
                :model-value="
                  props.ataColumns.amount?.getValue(row) ??
                  formatNumber(row.amount)
                "
                @update:model-value="
                  emit('update-ata', row.id, 'amount', $event)
                "
              />
              kr
            </span>
          </td>
          <td v-if="props.columns.amountDue"></td>
          <td v-if="props.columns.invoicedDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.ataColumns.invoicedDate?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="props.ataColumns.invoicedDate?.getValue(row) ?? ''"
              @update:model-value="
                emit('update-ata', row.id, 'invoicedDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.paymentDate" class="col-date">
            <StateInput
              type="date"
              :mode="
                props.ataColumns.paymentDate?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input"
              inputmode="numeric"
              placeholder="åååå-mm-dd"
              :model-value="props.ataColumns.paymentDate?.getValue(row) ?? ''"
              @update:model-value="
                emit('update-ata', row.id, 'paymentDate', $event)
              "
            />
          </td>
          <td v-if="props.columns.comment" class="comment-cell">
            <StateInput
              :mode="
                props.ataColumns.comment?.state === 'edit'
                  ? 'edit'
                  : 'read-only'
              "
              class="inline-input comment-input"
              :model-value="props.ataColumns.comment?.getValue(row) ?? ''"
              @update:model-value="
                emit('update-ata', row.id, 'comment', $event)
              "
            />
          </td>
        </tr>
        <tr v-if="props.componentState.addAta !== 'hidden'" class="add-ata-row">
          <td :colspan="addRowColspan" class="add-row-cell">
            <button
              class="row-add-btn"
              :disabled="props.componentState.addAta === 'disabled'"
              @click="emit('add-ata')"
            >
              <PhPlus :size="13" />
              {{ t('projects.liftPlanUE.liftTable.addAta') }}
            </button>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<style scoped>
.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.amount-input {
  flex: 1;
  width: 0;
  min-width: 0;
  text-align: right;
}

.table {
  min-width: 100%;
}

.table th,
.table td {
  padding: 0.6rem 0.85rem;
  vertical-align: middle;
}

.col-number {
  width: 6rem;
  white-space: nowrap;
}

.number-input {
  width: 2rem;
  text-align: left;
}

.col-description {
  min-width: 8rem;
}

.col-date {
  width: 7rem;
}

.col-amount {
  width: 8rem;
  text-align: right !important;
  white-space: nowrap;
}

.amount-field {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
  justify-content: flex-end;
}

.entry-number {
  color: var(--primary-color);
  font-weight: 600;
}

.muted {
  color: #999;
  font-size: 0.85rem;
}

.table tfoot tr.totals-row td {
  font-weight: 700;
  font-size: inherit;
  border-top: 2px solid #ddd;
}

.subtotals-cell {
  font-weight: 400 !important;
}

.totals-label {
  color: #555;
  font-size: 0.8rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.subtotals-line {
  display: flex;
  justify-content: space-between;
}

.col-action {
  width: 2.5rem;
  padding-left: 0.5rem !important;
  padding-right: 0 !important;
  text-align: center;
}

.row-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: #fee2e2;
  color: #dc2626;
  transition:
    background 0.15s,
    opacity 0.15s;
  margin: 0 auto;
}

.row-delete-btn:hover {
  background: #fca5a5;
}

.row-delete-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.row-add-btn {
  display: inline-flex;
  align-items: center;
  background: var(--primary-color);
  color: #fff;
  width: auto;
  height: auto;
  border-radius: 999px;
  padding: 0.2rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 0.25rem;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;
  font-smoothing: antialiased;
}

.row-add-btn:hover {
  opacity: 0.85;
}

.add-row {
  background: color-mix(in srgb, var(--primary-color) 5%, white);
}

.add-row > td {
  border-bottom: none;
}

.add-row-cell {
  text-align: left;
  padding: 0.5rem 0.85rem !important;
}

.description-input {
  width: 100%;
  min-width: 0;
}

.col-date :deep(.date-edit-field) {
  width: 7.25rem;
}

.label-input {
  width: 100%;
}

.subtotal-label-cell {
  position: relative;
}

.add-subtotal-row,
.add-ata-row {
  background: color-mix(in srgb, var(--primary-color) 5%, white);
  border-top: 1.5px dashed var(--primary-color);
}

.add-subtotal-row > td,
.add-ata-row > td {
  border-bottom: none;
}

.ata-section-header-row > th {
  background: color-mix(in srgb, var(--primary-color) 12%, white);
  border-top: 2px solid var(--primary-color);
  color: var(--primary-color);
  font-weight: 700;
}

.ata-row {
  background: color-mix(in srgb, var(--primary-color) 5%, white);
}

.comment-cell {
  min-width: 8rem;
}

.comment-input {
  width: 100%;
  min-width: 0;
}

.table-wrapper {
  overflow-x: auto;
  background-color: #fff;
}
</style>
